import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { AdminFormDialog } from '@/components/shared/AdminFormDialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useArchiveLicenseRecipe,
  useCreateLicenseRecipe,
  useLicenseRecipes,
  useLicensesAttentionWhatsAppPhone,
  useToggleLicenseRecipe,
  useUpdateLicenseRecipe,
  useUpdateLicensesAttentionWhatsAppPhone,
} from './hooks/useLicenseOrders';
import {
  CCAA_OPTIONS,
  CHECKOUT_REQUIREMENT_OPTIONS,
  FEE_VARIANT_KIND_OPTIONS,
  MODALITY_OPTIONS,
  ccaaLabel,
  checkoutRequirementLabel,
  modalityLabel,
  subterritorioLabel,
  subterritorioOptionsFor,
} from './ccaa';
import type {
  LicenseCheckoutRequirement,
  LicenseDurationOption,
  LicenseFeeVariant,
  LicenseFeeVariantKind,
  LicenseRecipe,
  LicenseSaleMode,
} from './types';
import {
  durationMissingValidityError,
  recipeHasDurationWithoutValidity,
} from './recipeDurationValidity';

const DURATION_DEFINITIONS = [
  { code: '1y', label: '1 año', durationMap: '1' },
  { code: '2y', label: '2 años', durationMap: '2' },
  { code: '3y', label: '3 años', durationMap: '3' },
  { code: '4y', label: '4 años', durationMap: '4' },
  { code: '5y', label: '5 años', durationMap: '5' },
  { code: 'resident_1y', label: 'Residente · 1 año', durationMap: '1' },
  { code: 'resident_2y', label: 'Residente · 2 años', durationMap: '2' },
  { code: 'resident_3y', label: 'Residente · 3 años', durationMap: '3' },
  { code: 'resident_4y', label: 'Residente · 4 años', durationMap: '4' },
  { code: 'resident_5y', label: 'Residente · 5 años', durationMap: '5' },
  { code: 'minor_1y', label: 'Menor · 1 año', durationMap: '1', exemption: 'minor' },
  { code: 'minor_2y', label: 'Menor · 2 años', durationMap: '2', exemption: 'minor' },
  { code: 'minor_3y', label: 'Menor · 3 años', durationMap: '3', exemption: 'minor' },
  { code: 'minor_4y', label: 'Menor · 4 años', durationMap: '4', exemption: 'minor' },
  { code: 'minor_5y', label: 'Menor · 5 años', durationMap: '5', exemption: 'minor' },
  { code: 'resident_senior', label: 'Pensionista residente', durationMap: '1', exemption: 'pensionista' },
  { code: 'senior_65', label: 'Mayores de 65', durationMap: '1' },
] as const;

type RecipeDraft = {
  ccaa: string;
  modality: string;
  subterritorio: string;
  tier: string;
  status: 'draft' | 'active' | 'paused';
  saleMode: LicenseSaleMode;
  whatsappMessage: string;
  displayName: string;
  sedeUrl: string;
  durationOptions: LicenseDurationOption[];
  checkoutRequirements: LicenseCheckoutRequirement[];
};

function newDuration(code: string): LicenseDurationOption {
  const definition = DURATION_DEFINITIONS.find((item) => item.code === code)
    ?? DURATION_DEFINITIONS[0];
  return {
    code: definition.code,
    label: definition.label,
    officialFeeCents: 0,
    durationMap: definition.durationMap,
    exemption: 'exemption' in definition ? definition.exemption : undefined,
    feeVariants: [],
  };
}

function emptyDraft(): RecipeDraft {
  return {
    ccaa: 'cantabria',
    modality: 'continental',
    subterritorio: '',
    tier: 'T1',
    status: 'draft',
    saleMode: 'online',
    whatsappMessage: '',
    displayName: '',
    sedeUrl: '',
    durationOptions: [newDuration('1y')],
    checkoutRequirements: [],
  };
}

function recipeDraft(recipe: LicenseRecipe): RecipeDraft {
  return {
    ccaa: recipe.ccaa,
    modality: recipe.modality,
    subterritorio: recipe.subterritorio || '',
    tier: recipe.tier,
    status: recipe.status === 'active' || recipe.status === 'paused' ? recipe.status : 'draft',
    saleMode: recipe.saleMode === 'whatsapp' ? 'whatsapp' : 'online',
    whatsappMessage: recipe.whatsappMessage || '',
    displayName: recipe.displayName,
    sedeUrl: recipe.sedeUrl,
    durationOptions: recipe.durationOptions.map((duration) => ({
      ...duration,
      feeVariants: duration.feeVariants ? duration.feeVariants.map((v) => ({ ...v })) : [],
    })),
    checkoutRequirements: [...recipe.checkoutRequirements],
  };
}

function fmtEur(cents: number): string {
  return `${((cents || 0) / 100).toLocaleString('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}\u00a0€`;
}

function newFeeVariant(): LicenseFeeVariant {
  return {
    code: '',
    label: '',
    officialFeeCents: 0,
    kind: 'general',
  };
}

export function LicenseRecipeManager() {
  const { data: recipes, isLoading, error } = useLicenseRecipes();
  const createRecipe = useCreateLicenseRecipe();
  const updateRecipe = useUpdateLicenseRecipe();
  const archiveRecipe = useArchiveLicenseRecipe();
  const toggleRecipe = useToggleLicenseRecipe();
  const { data: attentionPhone = '' } = useLicensesAttentionWhatsAppPhone();
  const updateAttentionPhone = useUpdateLicensesAttentionWhatsAppPhone();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<LicenseRecipe | null>(null);
  const [draft, setDraft] = useState<RecipeDraft>(emptyDraft);
  const [formError, setFormError] = useState<string | null>(null);
  const [durationToAdd, setDurationToAdd] = useState('2y');
  const [deletingRecipe, setDeletingRecipe] = useState<LicenseRecipe | null>(null);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [phoneSavedHint, setPhoneSavedHint] = useState(false);

  const availableDurations = useMemo(
    () => DURATION_DEFINITIONS.filter(
      (definition) => !draft.durationOptions.some((duration) => duration.code === definition.code)
    ),
    [draft.durationOptions]
  );

  const subterritorioChoices = useMemo(
    () => subterritorioOptionsFor(draft.ccaa, draft.modality),
    [draft.ccaa, draft.modality]
  );

  useEffect(() => {
    setPhoneDraft(attentionPhone);
  }, [attentionPhone]);

  const openCreate = () => {
    setEditingRecipe(null);
    setDraft(emptyDraft());
    setFormError(null);
    setDurationToAdd('2y');
    setDialogOpen(true);
  };

  const openEdit = (recipe: LicenseRecipe) => {
    setEditingRecipe(recipe);
    setDraft(recipeDraft(recipe));
    setFormError(null);
    setDurationToAdd('1y');
    setDialogOpen(true);
  };

  const closeEditor = () => {
    setDialogOpen(false);
    setEditingRecipe(null);
    setFormError(null);
  };

  const updateDuration = (
    code: string,
    patch: Partial<LicenseDurationOption>
  ) => {
    setDraft((previous) => ({
      ...previous,
      durationOptions: previous.durationOptions.map((item) =>
        item.code === code ? { ...item, ...patch } : item
      ),
    }));
  };

  const save = async () => {
    const name = draft.displayName.trim();
    const sedeUrl = draft.sedeUrl.trim();
    if (!name) {
      setFormError('El nombre es obligatorio.');
      return;
    }
    if (!/^https?:\/\//i.test(sedeUrl)) {
      setFormError('La URL de la sede debe empezar por http:// o https://.');
      return;
    }
    if (!draft.durationOptions.length) {
      setFormError('Añade al menos una duración.');
      return;
    }
    for (const duration of draft.durationOptions) {
      if (!duration.label.trim()) {
        setFormError(`La duración ${duration.code} necesita una etiqueta.`);
        return;
      }
      const labelEn = (duration.labelEn || '').trim();
      if (labelEn.length > 80) {
        setFormError(`La etiqueta en inglés de ${duration.code} no puede superar 80 caracteres.`);
        return;
      }
      const validityError = durationMissingValidityError(duration);
      if (validityError) {
        setFormError(validityError);
        return;
      }
      for (const variant of duration.feeVariants || []) {
        if (!variant.code.trim() || !variant.label.trim()) {
          setFormError(`Variante incompleta en ${duration.code}.`);
          return;
        }
        const variantLabelEn = (variant.labelEn || '').trim();
        if (variantLabelEn.length > 80) {
          setFormError(
            `La etiqueta en inglés de la variante ${variant.code} (${duration.code}) no puede superar 80 caracteres.`
          );
          return;
        }
      }
    }
    if (subterritorioChoices.length && draft.subterritorio
      && !subterritorioChoices.includes(draft.subterritorio)) {
      setFormError('Subterritorio no válido para esta CCAA/modalidad.');
      return;
    }

    setFormError(null);
    const durationOptions = draft.durationOptions.map((duration) => ({
      ...duration,
      label: duration.label.trim(),
      labelEn: (duration.labelEn || '').trim().slice(0, 80) || null,
      feeVariants: (duration.feeVariants || []).map((variant) => ({
        ...variant,
        code: variant.code.trim(),
        label: variant.label.trim(),
        labelEn: (variant.labelEn || '').trim().slice(0, 80) || null,
      })),
    }));
    try {
      if (editingRecipe) {
        await updateRecipe.mutateAsync({
          recipeId: editingRecipe.id,
          tier: draft.tier,
          sedeUrl,
          displayName: name,
          durationOptions,
          subterritorio: draft.subterritorio || '',
          checkoutRequirements: draft.checkoutRequirements,
          saleMode: draft.saleMode,
          whatsappMessage: draft.whatsappMessage,
        });
      } else {
        await createRecipe.mutateAsync({
          ...draft,
          displayName: name,
          sedeUrl,
          durationOptions,
          subterritorio: draft.subterritorio || '',
          saleMode: draft.saleMode,
          whatsappMessage: draft.whatsappMessage,
        });
      }
      closeEditor();
    } catch (saveError) {
      setFormError(saveError instanceof Error ? saveError.message : 'No se pudo guardar la licencia.');
    }
  };

  const confirmArchive = async () => {
    if (!deletingRecipe) return;
    try {
      await archiveRecipe.mutateAsync(deletingRecipe.id);
      setDeletingRecipe(null);
    } catch (archiveError) {
      window.alert(
        archiveError instanceof Error ? archiveError.message : 'No se pudo borrar la licencia.'
      );
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-10"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-muted-foreground">
          El catálogo público se actualiza desde estas recetas. La tasa oficial se suma al honorario
          configurado en Gestiones. Puedes añadir modalidades, subterritorios y duraciones dentro
          de las CCAA admitidas. El modo «Solo por WhatsApp» muestra la receta en el catálogo
          pero bloquea la compra online.
        </p>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
        >
          <Plus size={15} />
          Nueva licencia
        </button>
      </div>

      <div className="rounded-xl border border-border bg-muted/20 p-4">
        <div className="text-sm font-medium">WhatsApp de atención (licencias)</div>
        <p className="mt-1 text-xs text-muted-foreground">
          Solo dígitos con prefijo país (ej. 346XXXXXXXX). Se usa en los enlaces wa.me del
          catálogo público para recetas en modo WhatsApp.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="min-w-[14rem] flex-1 space-y-1 text-sm">
            <span className="text-muted-foreground">Número</span>
            <input
              value={phoneDraft}
              onChange={(event) => {
                setPhoneDraft(event.target.value.replace(/[^\d]/g, ''));
                setPhoneSavedHint(false);
              }}
              placeholder="346XXXXXXXX"
              className="w-full rounded-md border border-border bg-background px-3 py-2"
            />
          </label>
          <button
            type="button"
            disabled={updateAttentionPhone.isPending}
            onClick={() => {
              void updateAttentionPhone.mutateAsync(phoneDraft).then(() => {
                setPhoneSavedHint(true);
              }).catch((phoneError) => {
                window.alert(
                  phoneError instanceof Error ? phoneError.message : 'No se pudo guardar el número.'
                );
              });
            }}
            className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {updateAttentionPhone.isPending ? 'Guardando…' : 'Guardar número'}
          </button>
          {phoneSavedHint ? (
            <span className="pb-2 text-xs text-emerald-400">Guardado</span>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
          {error.message}
        </div>
      ) : null}

      {(recipes || []).map((recipe) => (
        <div key={recipe.id} className="overflow-hidden rounded-xl border border-border">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/30 px-4 py-3">
            <div>
              <div className="font-semibold">{recipe.displayName}</div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>
                  {ccaaLabel(recipe.ccaa)}
                  {recipe.subterritorio ? ` · ${subterritorioLabel(recipe.subterritorio)}` : ''}
                  {' · '}{modalityLabel(recipe.modality)} · {recipe.tier} · {recipe.status}
                </span>
                {recipe.saleMode === 'whatsapp' ? (
                  <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-medium text-emerald-400 ring-1 ring-emerald-500/30">
                    WhatsApp
                  </span>
                ) : null}
              </div>
              {recipeHasDurationWithoutValidity(recipe) ? (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-300">
                  <AlertTriangle size={13} className="shrink-0" />
                  Hay duraciones sin vigencia en años; corrígelas al editar.
                </div>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => openEdit(recipe)}
                className="inline-flex items-center gap-1.5 rounded-md bg-muted px-3 py-1.5 text-xs hover:bg-muted/80"
              >
                <Pencil size={13} />
                Editar
              </button>
              <button
                type="button"
                disabled={toggleRecipe.isPending}
                onClick={() => {
                  void toggleRecipe.mutateAsync({
                    recipeId: recipe.id,
                    status: recipe.status === 'active' ? 'paused' : 'active',
                  }).catch((toggleError) => {
                    window.alert(toggleError instanceof Error ? toggleError.message : 'No se pudo cambiar el estado.');
                  });
                }}
                className="rounded-md bg-muted px-3 py-1.5 text-xs hover:bg-muted/80 disabled:opacity-50"
              >
                {recipe.status === 'active' ? 'Pausar venta' : 'Activar venta'}
              </button>
              <button
                type="button"
                disabled={recipe.hasPaidOrders}
                title={recipe.hasPaidOrders ? 'No se puede borrar porque ya tiene pagos' : 'Borrar licencia'}
                onClick={() => setDeletingRecipe(recipe)}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-500/25 bg-red-500/10 px-3 py-1.5 text-xs text-red-400 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Trash2 size={13} />
                Borrar
              </button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/20 text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-medium">Duración</th>
                <th className="px-4 py-2.5 font-medium">Vigencia (años)</th>
                <th className="px-4 py-2.5 font-medium">Tasa oficial</th>
              </tr>
            </thead>
            <tbody>
              {recipe.durationOptions.map((duration) => (
                <tr key={duration.code} className="border-t border-border">
                  <td className="px-4 py-2.5">{duration.label}</td>
                  <td className="px-4 py-2.5">{duration.durationMap || '—'}</td>
                  <td className="px-4 py-2.5">{fmtEur(duration.officialFeeCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {recipe.checkoutRequirements.length ? (
            <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
              Requisitos:{' '}
              {recipe.checkoutRequirements.map(checkoutRequirementLabel).join(' · ')}
            </div>
          ) : null}
        </div>
      ))}

      {!recipes?.length && !error ? (
        <div className="rounded-xl border border-border p-8 text-center text-muted-foreground">
          No hay licencias configuradas
        </div>
      ) : null}

      <AdminFormDialog
        open={dialogOpen}
        title={editingRecipe ? 'Editar licencia' : 'Nueva licencia'}
        description={editingRecipe
          ? `${ccaaLabel(editingRecipe.ccaa)} · ${modalityLabel(editingRecipe.modality)}`
          : 'Selecciona una CCAA existente y la modalidad que quieres ofrecer.'}
        error={formError}
        saving={createRecipe.isPending || updateRecipe.isPending}
        saveLabel={editingRecipe ? 'Guardar cambios' : 'Crear licencia'}
        maxWidthClass="max-w-3xl"
        onClose={closeEditor}
        onSave={() => { void save(); }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Comunidad autónoma</span>
            <select
              value={draft.ccaa}
              disabled={Boolean(editingRecipe)}
              onChange={(event) => {
                const ccaa = event.target.value;
                setDraft((previous) => {
                  const nextChoices = subterritorioOptionsFor(ccaa, previous.modality);
                  return {
                    ...previous,
                    ccaa,
                    subterritorio: nextChoices.includes(previous.subterritorio)
                      ? previous.subterritorio
                      : '',
                  };
                });
              }}
              className="w-full rounded-md border border-border bg-background px-3 py-2 disabled:opacity-60"
            >
              {CCAA_OPTIONS.map((ccaa) => <option key={ccaa} value={ccaa}>{ccaaLabel(ccaa)}</option>)}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Modalidad</span>
            <select
              value={draft.modality}
              disabled={Boolean(editingRecipe)}
              onChange={(event) => {
                const modality = event.target.value;
                setDraft((previous) => {
                  const nextChoices = subterritorioOptionsFor(previous.ccaa, modality);
                  return {
                    ...previous,
                    modality,
                    subterritorio: nextChoices.includes(previous.subterritorio)
                      ? previous.subterritorio
                      : '',
                  };
                });
              }}
              className="w-full rounded-md border border-border bg-background px-3 py-2 disabled:opacity-60"
            >
              {MODALITY_OPTIONS.map((modality) => (
                <option key={modality} value={modality}>{modalityLabel(modality)}</option>
              ))}
            </select>
          </label>
          {subterritorioChoices.length ? (
            <label className="space-y-1 text-sm sm:col-span-2">
              <span className="text-muted-foreground">Subterritorio (opcional)</span>
              <select
                value={draft.subterritorio}
                onChange={(event) => setDraft((previous) => ({
                  ...previous,
                  subterritorio: event.target.value,
                }))}
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              >
                <option value="">Sin subterritorio (receta general)</option>
                {subterritorioChoices.map((sub) => (
                  <option key={sub} value={sub}>{subterritorioLabel(sub)}</option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="space-y-1 text-sm sm:col-span-2">
            <span className="text-muted-foreground">Nombre público</span>
            <input
              value={draft.displayName}
              onChange={(event) => setDraft((previous) => ({ ...previous, displayName: event.target.value }))}
              placeholder="Licencia de pesca continental — Cantabria"
              className="w-full rounded-md border border-border bg-background px-3 py-2"
            />
          </label>
          <label className="space-y-1 text-sm sm:col-span-2">
            <span className="text-muted-foreground">URL de la sede</span>
            <input
              type="url"
              value={draft.sedeUrl}
              onChange={(event) => setDraft((previous) => ({ ...previous, sedeUrl: event.target.value }))}
              placeholder="https://sede..."
              className="w-full rounded-md border border-border bg-background px-3 py-2"
            />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Complejidad operativa</span>
            <select
              value={draft.tier}
              onChange={(event) => setDraft((previous) => ({ ...previous, tier: event.target.value }))}
              className="w-full rounded-md border border-border bg-background px-3 py-2"
            >
              {['T0', 'T1', 'T2', 'T3'].map((tier) => <option key={tier}>{tier}</option>)}
            </select>
          </label>
          {!editingRecipe ? (
            <label className="space-y-1 text-sm">
              <span className="text-muted-foreground">Estado inicial</span>
              <select
                value={draft.status}
                onChange={(event) => setDraft((previous) => ({
                  ...previous,
                  status: event.target.value as RecipeDraft['status'],
                }))}
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              >
                <option value="draft">Borrador</option>
                <option value="active">Activa en la web</option>
                <option value="paused">Pausada</option>
              </select>
            </label>
          ) : null}
          <label className="space-y-1 text-sm">
            <span className="text-muted-foreground">Modo de venta</span>
            <select
              value={draft.saleMode}
              onChange={(event) => setDraft((previous) => ({
                ...previous,
                saleMode: event.target.value as LicenseSaleMode,
              }))}
              className="w-full rounded-md border border-border bg-background px-3 py-2"
            >
              <option value="online">Online</option>
              <option value="whatsapp">Solo por WhatsApp</option>
            </select>
          </label>
          {draft.saleMode === 'whatsapp' ? (
            <label className="space-y-1 text-sm sm:col-span-2">
              <span className="text-muted-foreground">Mensaje prellenado de WhatsApp</span>
              <textarea
                value={draft.whatsappMessage}
                onChange={(event) => setDraft((previous) => ({
                  ...previous,
                  whatsappMessage: event.target.value.slice(0, 500),
                }))}
                rows={3}
                placeholder="Hola, quiero información sobre esta licencia…"
                className="w-full rounded-md border border-border bg-background px-3 py-2"
              />
            </label>
          ) : null}
        </div>

        <div className="space-y-2 border-t border-border pt-3">
          <div className="text-sm font-medium">Requisitos del checkout</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {CHECKOUT_REQUIREMENT_OPTIONS.map((flag) => {
              const checked = draft.checkoutRequirements.includes(flag);
              return (
                <label key={flag} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      setDraft((previous) => ({
                        ...previous,
                        checkoutRequirements: event.target.checked
                          ? [...previous.checkoutRequirements, flag]
                          : previous.checkoutRequirements.filter((item) => item !== flag),
                      }));
                    }}
                  />
                  {checkoutRequirementLabel(flag)}
                </label>
              );
            })}
          </div>
        </div>

        <div className="space-y-2 border-t border-border pt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-medium">Duraciones y tasas</div>
              <div className="text-xs text-muted-foreground">
                Etiqueta ES · etiqueta EN opcional (correos) · vigencia (años) · variantes de tasa.
              </div>
            </div>
            {availableDurations.length ? (
              <div className="flex gap-2">
                <select
                  value={availableDurations.some((item) => item.code === durationToAdd)
                    ? durationToAdd
                    : availableDurations[0].code}
                  onChange={(event) => setDurationToAdd(event.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                >
                  {availableDurations.map((item) => (
                    <option key={item.code} value={item.code}>{item.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const code = availableDurations.some((item) => item.code === durationToAdd)
                      ? durationToAdd
                      : availableDurations[0].code;
                    setDraft((previous) => ({
                      ...previous,
                      durationOptions: [...previous.durationOptions, newDuration(code)],
                    }));
                  }}
                  className="rounded-md bg-muted px-2.5 py-1.5 text-sm"
                >
                  Añadir
                </button>
              </div>
            ) : null}
          </div>

          {draft.durationOptions.map((duration) => (
            <div key={duration.code} className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="min-w-[10rem] flex-1 space-y-1 text-sm">
                  <span className="text-muted-foreground">Etiqueta ({duration.code})</span>
                  <input
                    value={duration.label}
                    onChange={(event) => updateDuration(duration.code, { label: event.target.value })}
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5"
                  />
                </label>
                <label className="min-w-[10rem] flex-1 space-y-1 text-sm">
                  <span className="text-muted-foreground">Etiqueta en inglés</span>
                  <input
                    value={duration.labelEn ?? ''}
                    maxLength={80}
                    placeholder="Opcional · correos EN"
                    onChange={(event) =>
                      updateDuration(duration.code, {
                        labelEn: event.target.value.slice(0, 80),
                      })
                    }
                    onBlur={(event) =>
                      updateDuration(duration.code, {
                        labelEn: event.target.value.trim().slice(0, 80) || null,
                      })
                    }
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground">Vigencia años</span>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    disabled={duration.durationMap === null}
                    value={duration.durationMap === null ? '' : (duration.durationMap || '')}
                    onChange={(event) => updateDuration(duration.code, {
                      durationMap: event.target.value,
                    })}
                    className="w-20 rounded-md border border-border bg-background px-2 py-1.5 disabled:opacity-50"
                  />
                </label>
                <label className="flex items-center gap-2 pb-2 text-sm">
                  <input
                    type="checkbox"
                    checked={duration.durationMap === null}
                    onChange={(event) => updateDuration(duration.code, {
                      durationMap: event.target.checked ? null : (duration.durationMap || '1'),
                    })}
                  />
                  Indefinida
                </label>
                <label className="space-y-1 text-sm">
                  <span className="text-muted-foreground">Tasa €</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={(duration.officialFeeCents / 100).toFixed(2)}
                    onChange={(event) => {
                      const cents = Math.max(0, Math.round(Number(event.target.value || 0) * 100));
                      updateDuration(duration.code, { officialFeeCents: cents });
                    }}
                    className="w-24 rounded-md border border-border bg-background px-2 py-1.5"
                  />
                </label>
                {!duration.code.startsWith('resident_') && !duration.code.startsWith('minor_') ? (
                  <label className="flex items-center gap-2 pb-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(duration.exemption)}
                      onChange={(event) => updateDuration(duration.code, {
                        exemption: event.target.checked ? (duration.exemption || 'exemption') : undefined,
                      })}
                    />
                    Exención (justificante)
                  </label>
                ) : null}
                <button
                  type="button"
                  title="Quitar duración"
                  onClick={() => setDraft((previous) => ({
                    ...previous,
                    durationOptions: previous.durationOptions.filter((item) => item.code !== duration.code),
                  }))}
                  className="rounded-md p-1.5 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="space-y-2 rounded-md bg-muted/20 p-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium text-muted-foreground">
                    Variantes de tasa (eslora / residente / recargo)
                  </div>
                  <button
                    type="button"
                    onClick={() => updateDuration(duration.code, {
                      feeVariants: [...(duration.feeVariants || []), newFeeVariant()],
                    })}
                    className="rounded-md bg-muted px-2 py-1 text-xs"
                  >
                    Añadir variante
                  </button>
                </div>
                {(duration.feeVariants || []).map((variant, index) => (
                  <div key={`${duration.code}-var-${index}`} className="flex flex-wrap items-end gap-2">
                    <label className="space-y-1 text-xs">
                      <span className="text-muted-foreground">Código</span>
                      <input
                        value={variant.code}
                        onChange={(event) => {
                          const feeVariants = [...(duration.feeVariants || [])];
                          feeVariants[index] = { ...variant, code: event.target.value };
                          updateDuration(duration.code, { feeVariants });
                        }}
                        placeholder="eslora_le6"
                        className="w-28 rounded-md border border-border bg-background px-2 py-1"
                      />
                    </label>
                    <label className="min-w-[8rem] flex-1 space-y-1 text-xs">
                      <span className="text-muted-foreground">Etiqueta</span>
                      <input
                        value={variant.label}
                        onChange={(event) => {
                          const feeVariants = [...(duration.feeVariants || [])];
                          feeVariants[index] = { ...variant, label: event.target.value };
                          updateDuration(duration.code, { feeVariants });
                        }}
                        placeholder="Embarcación ≤6 m"
                        className="w-full rounded-md border border-border bg-background px-2 py-1"
                      />
                    </label>
                    <label className="min-w-[8rem] flex-1 space-y-1 text-xs">
                      <span className="text-muted-foreground">Etiqueta en inglés</span>
                      <input
                        value={variant.labelEn ?? ''}
                        maxLength={80}
                        placeholder="Opcional · Checkout/correos EN"
                        onChange={(event) => {
                          const feeVariants = [...(duration.feeVariants || [])];
                          feeVariants[index] = {
                            ...variant,
                            labelEn: event.target.value.slice(0, 80),
                          };
                          updateDuration(duration.code, { feeVariants });
                        }}
                        onBlur={(event) => {
                          const feeVariants = [...(duration.feeVariants || [])];
                          feeVariants[index] = {
                            ...variant,
                            labelEn: event.target.value.trim().slice(0, 80) || null,
                          };
                          updateDuration(duration.code, { feeVariants });
                        }}
                        className="w-full rounded-md border border-border bg-background px-2 py-1"
                      />
                    </label>
                    <label className="space-y-1 text-xs">
                      <span className="text-muted-foreground">Tipo</span>
                      <select
                        value={variant.kind || 'general'}
                        onChange={(event) => {
                          const feeVariants = [...(duration.feeVariants || [])];
                          feeVariants[index] = {
                            ...variant,
                            kind: event.target.value as LicenseFeeVariantKind,
                          };
                          updateDuration(duration.code, { feeVariants });
                        }}
                        className="rounded-md border border-border bg-background px-2 py-1"
                      >
                        {FEE_VARIANT_KIND_OPTIONS.map((kind) => (
                          <option key={kind.value} value={kind.value}>{kind.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="space-y-1 text-xs">
                      <span className="text-muted-foreground">Tasa €</span>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={(variant.officialFeeCents / 100).toFixed(2)}
                        onChange={(event) => {
                          const cents = Math.max(0, Math.round(Number(event.target.value || 0) * 100));
                          const feeVariants = [...(duration.feeVariants || [])];
                          feeVariants[index] = { ...variant, officialFeeCents: cents };
                          updateDuration(duration.code, { feeVariants });
                        }}
                        className="w-20 rounded-md border border-border bg-background px-2 py-1"
                      />
                    </label>
                    <label className="flex items-center gap-1 pb-1 text-xs">
                      <input
                        type="checkbox"
                        checked={Boolean(variant.additive)}
                        onChange={(event) => {
                          const feeVariants = [...(duration.feeVariants || [])];
                          feeVariants[index] = { ...variant, additive: event.target.checked };
                          updateDuration(duration.code, { feeVariants });
                        }}
                      />
                      Recargo
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const feeVariants = (duration.feeVariants || []).filter((_, i) => i !== index);
                        updateDuration(duration.code, { feeVariants });
                      }}
                      className="rounded-md p-1 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </AdminFormDialog>

      <Dialog open={Boolean(deletingRecipe)} onOpenChange={(open) => { if (!open) setDeletingRecipe(null); }}>
        <DialogContent aria-describedby="delete-license-description" className="max-w-md bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-red-400" />
              Borrar licencia
            </DialogTitle>
          </DialogHeader>
          <p id="delete-license-description" className="text-sm text-muted-foreground">
            {deletingRecipe?.displayName} dejará de aparecer en la web y en la lista activa.
            Los pedidos iniciados se conservarán. Solo es posible si nunca hubo un pago.
          </p>
          <DialogFooter className="gap-2 sm:space-x-0">
            <button type="button" onClick={() => setDeletingRecipe(null)} className="rounded-lg px-4 py-2 text-sm">
              Cancelar
            </button>
            <button
              type="button"
              disabled={archiveRecipe.isPending}
              onClick={() => { void confirmArchive(); }}
              className="rounded-lg border border-red-500/25 bg-red-500/10 px-4 py-2 text-sm text-red-400 disabled:opacity-50"
            >
              {archiveRecipe.isPending ? 'Borrando…' : 'Borrar licencia'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
